import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, MapPin, Package, Route as RouteIcon, ScrollText, ShieldCheck, ShoppingCart, Trophy } from "lucide-react";
import { Card, EmptyState } from "@/components/ranger/PageHeader";
import { itemsFmt } from "@/components/ranger/Crud";
import { BoardTabs, Leaderboard } from "@/pages/Feeds";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { api } from "@/lib/api";

const ZONE_TONE = { pvp: "text-[#E5202E]", safe: "text-emerald-400", nobuild: "text-amber-400" };
const MODES = { pve: "PvE", pvp: "PvP", pve_purge: "PvE · Purge", pvp_purge: "PvP · Purge" };

const ItemList = ({ rows, testId, cur, render }) => rows.length === 0 ? <p className="text-sm text-slate-500">Nothing listed yet.</p> : (
  <ul data-testid={testId} className="divide-y divide-[#1E2D4A]">{rows.map((r) => (
    <li key={r.id} className="py-2.5">
      <div className="flex items-center justify-between gap-3"><span className="font-semibold text-slate-100">{r.name}</span><span className="font-mono text-sm text-[#E5202E]">{r.price ? `${r.price} ${cur}` : "Free"}</span></div>
      {r.description && r.description.trim().toLowerCase() !== (render ? render(r) : itemsFmt(r.items)).replace(/× /g, " ").toLowerCase() && <p className="text-xs text-slate-400">{r.description}</p>}
      <p className="mt-0.5 font-mono text-xs text-slate-500">{render ? render(r) : itemsFmt(r.items)}</p>
    </li>
  ))}</ul>
);

const kitLine = (k) => [k.mode !== "fixed" ? `${k.mode} contents` : itemsFmt(k.items), k.cooldown_minutes ? `every ${Math.round(k.cooldown_minutes / 60) || 1}h` : null, k.vip ? "VIP" : null].filter(Boolean).join(" · ");

const Boards = ({ d }) => {
  const [b, setB] = useState("pvp");
  return <Card title="Leaderboards" icon={Trophy} testId="public-leaderboards" right={<BoardTabs value={b} onChange={setB} />}><div className="overflow-x-auto"><Leaderboard rows={d.leaderboards[b] || []} board={b} currency={d.currency} /></div></Card>;
};

const BattlePass = ({ bp, cur }) => (
  <Card title={`Battle pass · season ${bp.season}`} icon={Trophy} testId="public-battlepass">
    {bp.levels.length === 0 ? <p className="text-sm text-slate-500">No levels yet.</p> : (
      <ul className="max-h-96 divide-y divide-[#1E2D4A] overflow-y-auto">{bp.levels.map((l) => (
        <li key={l.level} className="flex flex-wrap items-baseline gap-x-3 py-2 text-xs">
          <span className="w-10 font-mono text-slate-500">L{l.level}</span>
          <span className="font-semibold text-slate-200">{l.title || `Level ${l.level}`}</span>
          <span className="font-mono text-slate-500">{l.xp_required} XP</span>
          <span className="text-slate-300">{[l.reward_coins ? `${l.reward_coins} ${cur}` : null, itemsFmt(l.items) || null].filter(Boolean).join(" + ")}</span>
          {(l.premium_coins > 0 || l.premium_items.length > 0) && <span className="text-amber-300">Premium: {[l.premium_coins ? `${l.premium_coins} ${cur}` : null, itemsFmt(l.premium_items) || null].filter(Boolean).join(" + ")}</span>}
        </li>
      ))}</ul>
    )}
  </Card>
);

const Travel = ({ d }) => {
  const p = d.protection;
  const on = [["Offline raid protection", p.orp], ["RaidLink alerts", p.raid_alerts], ["TP home", p.tp_home && p.teleports], ["Teleports", p.teleports]];
  return (
    <Card title="Protection & travel" icon={ShieldCheck} testId="public-protection">
      <ul className="mb-4 flex flex-wrap gap-2">{on.map(([l, v]) => <li key={l} className={`rounded-sm border px-2 py-1 font-mono text-[11px] uppercase ${v ? "border-emerald-500/40 text-emerald-400" : "border-[#1E2D4A] text-slate-500"}`}>{l} {v ? "on" : "off"}</li>)}</ul>
      {d.portals.length > 0 && <div className="mb-4"><div className="rg-label mb-1 flex items-center gap-1"><RouteIcon className="h-3 w-3" />Teleport locations</div><p data-testid="public-portals" className="text-sm text-slate-300">{d.portals.join(" · ")}</p></div>}
      {d.zones.length > 0 && <div><div className="rg-label mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" />Zones</div><ul data-testid="public-zones" className="space-y-1 text-xs text-slate-300">{d.zones.map((z) => <li key={z.id}><span className={`uppercase ${ZONE_TONE[z.kind]}`}>{z.kind}</span> {z.name} · {z.x}, {z.z} · r{z.radius}m</li>)}</ul></div>}
    </Card>
  );
};

const Header = ({ d }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div><div className="rg-label text-[#E5202E]">{MODES[d.mode] || d.mode}</div><h1 data-testid="public-server-name" className="mt-1 text-4xl font-bold uppercase tracking-wide text-slate-100 sm:text-5xl">{d.name}</h1>{d.tagline && <p className="mt-2 text-sm text-slate-400">{d.tagline}</p>}</div>
    <div className="flex flex-wrap gap-2">
      <Link data-testid="public-portal-link" to="/portal" className="rounded-sm bg-[#E5202E] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#c41a26]">My stats & rewards</Link>
      {d.discord_invite && <a data-testid="public-join" href={d.discord_invite} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-sm bg-[#5865F2] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#4752c4]">Join Discord<ExternalLink className="h-3 w-3" /></a>}
    </div>
  </div>
);

export default function ServerPublic() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/servers/${id}/overview`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);
  return (
    <div className="min-h-screen bg-[#050811] rg-grid-bg">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[#1E2D4A] bg-[#070A14] px-4 py-3 sm:px-6">
        <Link to="/browse" className="flex items-center gap-3"><img src="/ranger-logo.jpg" alt="RANGER" className="h-9 w-9 rounded-sm" /><span className="font-display text-xl font-bold uppercase tracking-[0.25em] text-slate-100">Ranger</span></Link>
        <div className="flex gap-4 text-xs"><Link data-testid="public-browse-link" to="/browse" className="text-slate-300 hover:text-white">All servers</Link><Link to="/login" className="text-slate-300 hover:text-white">Sign in</Link></div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10">
        {err ? <EmptyState title="Server not found" testId="public-not-found">This server isn't on the public list.</EmptyState> : !d ? <p className="font-mono text-xs text-slate-500">Loading…</p> : (
          <div className="space-y-5">
            <Header d={d} />
            {d.description && <p className="max-w-3xl whitespace-pre-wrap text-sm text-slate-300">{d.description}</p>}
            <Boards d={d} />
            <div className="grid gap-5 lg:grid-cols-2">
              <Card title="Shop" icon={ShoppingCart} testId="public-shop"><ItemList rows={d.shop} testId="public-shop-list" cur={d.currency} /></Card>
              <Card title="Kits" icon={Package} testId="public-kits"><ItemList rows={d.kits} testId="public-kit-list" cur={d.currency} render={kitLine} /></Card>
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              {d.battlepass.enabled && <BattlePass bp={d.battlepass} cur={d.currency} />}
              <Travel d={d} />
            </div>
            {d.rules.length > 0 && <Card title="Rules" icon={ScrollText} testId="public-rules"><ol className="list-decimal space-y-2 pl-5 text-sm text-slate-300">{d.rules.map((r) => <li key={r.title}><span className="font-semibold text-slate-100">{r.title}</span> — {r.body}</li>)}</ol></Card>}
            <p className="text-xs text-slate-500">Buying, claiming kits, daily rewards and teleports happen in the player portal or Discord after you link your gamertag with /link.</p>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
